/**
 * Generates a printable HTML provision settlement report.
 * @param {Object} batch - The provision batch object
 * @param {Object} summary - The batch summary containing areaSummaries and totals
 * @param {Array} selectedAreas - Array of area codes to include in the report
 * @param {string} [reference] - Optional reference/additional info string
 */
export function generateProvisionBatchReport(batch, summary, selectedAreas, reference) {
    const filteredAreas = summary.areaSummaries.filter(a =>
        selectedAreas.includes(a.area)
    );

    const formatCurrency = value =>
        new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR" }).format(value ?? 0);

    const formatPct = value =>
        new Intl.NumberFormat("fi-FI", {
            style: "percent",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format((value ?? 0) / 100);

    const formatDate = iso => {
        if (!iso) return "-";
        return new Date(iso).toLocaleDateString("fi-FI", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const today = new Date().toLocaleDateString("fi-FI", {
        year: "numeric", month: "2-digit", day: "2-digit"
    });

    // Compute totals for selected areas only
    const totals = filteredAreas.reduce((acc, a) => {
        acc.eventCount += a.eventCount ?? 0;
        acc.totalAcceptedCostGross += a.totalAcceptedCostGross ?? 0;
        acc.totalReimbursementNet += a.totalReimbursementNet ?? 0;
        acc.baseProvisionNet += a.baseProvisionNet ?? 0;
        acc.transactionProvisionNet += a.transactionProvisionNet ?? 0;
        acc.sourceProvisionNet += a.sourceProvisionNet ?? 0;
        acc.totalPurchaseInvoiceAmount += a.totalPurchaseInvoiceAmount ?? 0;
        acc.totalSalesInvoiceAmount += a.totalSalesInvoiceAmount ?? 0;
        acc.netToTaxiCompany += a.netToTaxiCompany ?? 0;
        return acc;
    }, {
        eventCount: 0,
        totalAcceptedCostGross: 0,
        totalReimbursementNet: 0,
        baseProvisionNet: 0,
        transactionProvisionNet: 0,
        sourceProvisionNet: 0,
        totalPurchaseInvoiceAmount: 0,
        totalSalesInvoiceAmount: 0,
        netToTaxiCompany: 0
    });

    const financialTableHtml = (a) => {
        const showSource = (a.sourceProvisionNet ?? 0) !== 0;
        return `
    <table class="financial-table">
      <thead>
        <tr>
          <th>Tapahtumat</th>
          <th>Kela-korvaus (brutto)</th>
          <th>Korvaus (netto)</th>
          <th>Perusprovisio</th>
          <th>Transaktioprovisio</th>
          ${showSource ? "<th>Lähdeprovisio</th>" : ""}
          <th>Ostolasku</th>
          <th>Myyntilasku</th>
          <th class="highlight-col">Netto taksiyritykselle</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="num">${(a.eventCount ?? 0).toLocaleString("fi-FI")}</td>
          <td class="num">${formatCurrency(a.totalAcceptedCostGross)}</td>
          <td class="num">${formatCurrency(a.totalReimbursementNet)}</td>
          <td class="num">${formatCurrency(a.baseProvisionNet)}</td>
          <td class="num">${formatCurrency(a.transactionProvisionNet)}</td>
          ${showSource ? `<td class="num">${formatCurrency(a.sourceProvisionNet)}</td>` : ""}
          <td class="num">${formatCurrency(a.totalPurchaseInvoiceAmount)}</td>
          <td class="num">${formatCurrency(a.totalSalesInvoiceAmount)}</td>
          <td class="num highlight-col"><strong>${formatCurrency(a.netToTaxiCompany)}</strong></td>
        </tr>
      </tbody>
    </table>`;
    };

    const provisionConfigHtml = (configs) => {
        if (!configs || configs.length === 0) return "";
        const rows = configs.map(c => `
      <tr>
        <td>${c.provisionType ?? ""}</td>
        <td class="num">${formatPct(c.percentage)}</td>
        <td>${c.description ?? ""}</td>
        <td>${c.calculationBase ?? ""}</td>
      </tr>`).join("");
        return `
      <div>
        <h4 class="section-sub-title">Provisioasetukset</h4>
        <table class="config-table">
          <thead>
            <tr>
              <th>Tyyppi</th>
              <th>%</th>
              <th>Kuvaus</th>
              <th>Laskentaperuste</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
    };

    const monthlyBreakdownHtml = (area) => {
        if (!area.monthlyBreakdown || area.monthlyBreakdown.length === 0) return "";
        const rows = area.monthlyBreakdown.map(m => `
      <tr>
        <td>${m.monthName}</td>
        <td class="num">${(m.eventCount ?? 0).toLocaleString("fi-FI")} kpl</td>
      </tr>`).join("");
        return `
      <div>
        <h4 class="section-sub-title">Kuukausittainen jakauma</h4>
        <table class="monthly-table">
          <thead>
            <tr><th>Kuukausi</th><th>Määrä</th></tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr class="total-row">
              <td><strong>Yhteensä</strong></td>
              <td class="num"><strong>${(area.eventCount ?? 0).toLocaleString("fi-FI")} kpl</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>`;
    };

    const areaSectionsHtml = filteredAreas.map(a => `
    <div class="area-card page-break-before">
      <div class="area-header">
        <span class="area-title">ALUE: ${a.area}</span>
        <span class="invoice-info">
          Ostolasku: ${a.purchaseInvoiceNumber ?? "-"} &nbsp;|&nbsp; Myyntilasku: ${a.salesInvoiceNumber ?? "-"}
        </span>
      </div>
      <div class="area-body">
        <h4 class="section-sub-title">Taloudellinen yhteenveto</h4>
        ${financialTableHtml(a)}
        <div class="sub-tables">
          ${provisionConfigHtml(a.provisionConfigurations)}
          ${monthlyBreakdownHtml(a)}
        </div>
      </div>
    </div>`).join("");

    const summaryHtml = filteredAreas.length > 1 ? `
    <div class="area-card summary-card">
      <div class="area-header summary-header">
        <span class="area-title">YHTEENVETO &ndash; KAIKKI VALITUT ALUEET</span>
      </div>
      <div class="area-body">
        ${financialTableHtml(totals)}
      </div>
    </div>` : "";

    const referenceHtml = reference ? `<div class="reference-line">Lisätieto / viite: ${reference}</div>` : "";

    const html = `<!DOCTYPE html>
<html lang="fi">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Provisiotilitys</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      font-size: 13px;
      background: #f0f2f5;
      color: #1a1a2e;
      padding: 0;
    }

    .page-wrapper {
      max-width: 1100px;
      margin: 0 auto;
      padding: 24px 16px 48px;
    }

    /* ── Header ── */
    .report-header {
      background: #1a237e;
      color: #fff;
      padding: 20px 28px;
      border-radius: 6px 6px 0 0;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 12px;
    }
    .report-title {
      font-size: 22px;
      font-weight: 700;
      letter-spacing: 1px;
    }
    .report-meta {
      font-size: 12px;
      line-height: 1.8;
      text-align: right;
      opacity: 0.9;
    }
    .report-meta strong { opacity: 1; }

    .reference-line {
      background: #e8eaf6;
      border-left: 4px solid #3949ab;
      padding: 8px 16px;
      font-size: 13px;
      margin-bottom: 0;
    }

    .divider {
      height: 4px;
      background: linear-gradient(90deg, #3949ab, #1e88e5);
      margin-bottom: 20px;
    }

    /* ── Area cards ── */
    .area-card {
      background: #fff;
      border-radius: 6px;
      box-shadow: 0 1px 4px rgba(0,0,0,0.10);
      margin-bottom: 20px;
      overflow: hidden;
    }

    .area-header {
      background: #283593;
      color: #fff;
      padding: 10px 18px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 8px;
    }
    .area-title {
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 0.5px;
    }
    .invoice-info {
      font-size: 12px;
      opacity: 0.85;
    }

    .area-body {
      padding: 16px 18px;
    }

    /* ── Summary card ── */
    .summary-card {
      margin-top: 24px;
    }
    .summary-header {
      background: #1565c0;
    }

    /* ── Section subtitles ── */
    .section-sub-title {
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #37474f;
      margin: 14px 0 6px;
    }
    .section-sub-title:first-child { margin-top: 0; }

    /* ── Financial table ── */
    .financial-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }
    .financial-table th {
      background: #e8eaf6;
      color: #283593;
      padding: 6px 8px;
      text-align: right;
      font-weight: 600;
      border: 1px solid #c5cae9;
      white-space: nowrap;
    }
    .financial-table td {
      padding: 6px 8px;
      border: 1px solid #e0e0e0;
    }
    .financial-table td.num, .financial-table th { text-align: right; }

    .highlight-col {
      background: #e3f2fd !important;
      color: #0d47a1 !important;
      font-weight: 700;
    }

    /* ── Config table ── */
    .config-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
      margin-bottom: 8px;
    }
    .config-table th {
      background: #fafafa;
      color: #37474f;
      padding: 5px 8px;
      text-align: left;
      border: 1px solid #e0e0e0;
      font-weight: 600;
    }
    .config-table td {
      padding: 5px 8px;
      border: 1px solid #eeeeee;
    }
    .config-table td.num { text-align: right; }

    /* ── Monthly table ── */
    .monthly-table {
      min-width: 220px;
      border-collapse: collapse;
      font-size: 12px;
    }
    .monthly-table th {
      background: #fafafa;
      color: #37474f;
      padding: 5px 10px;
      text-align: right;
      border: 1px solid #e0e0e0;
      font-weight: 600;
    }
    .monthly-table td {
      padding: 5px 10px;
      border: 1px solid #eeeeee;
      text-align: right;
    }
    .monthly-table tfoot .total-row td {
      background: #f5f5f5;
      border-top: 2px solid #bdbdbd;
    }

    .sub-tables {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-top: 4px;
    }

    /* ── Print buttons ── */
    .print-bar {
      text-align: center;
      padding: 20px 0 8px;
    }
    .btn {
      display: inline-block;
      padding: 9px 22px;
      border-radius: 4px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      border: none;
      margin: 0 6px;
    }
    .btn-primary {
      background: #1565c0;
      color: #fff;
    }
    .btn-secondary {
      background: #eceff1;
      color: #37474f;
      border: 1px solid #b0bec5;
    }

    /* ── Print styles ── */
    @page {
      size: A4 landscape;
      margin: 10mm 12mm;
    }

    @media print {
      body { background: #fff; font-size: 11px; }
      .page-wrapper { padding: 0; max-width: 100%; }
      .print-bar { display: none !important; }
      .area-card { box-shadow: none; border: 1px solid #ccc; }
      .page-break-before + .page-break-before { page-break-before: always; }
      .financial-table { font-size: 10px; }
      .financial-table th,
      .financial-table td { padding: 4px 5px; }
      .report-header { padding: 12px 16px; }
      .report-title { font-size: 16px; }
    }
  </style>
</head>
<body>
  <div class="page-wrapper">

    <div class="report-header">
      <div>
        <div class="report-title">PROVISIOTILITYS</div>
      </div>
      <div class="report-meta">
        <div>Luotu: <strong>${today}</strong></div>
        <div>Käsittelyaika: <strong>${formatDate(batch.startedAt)}</strong> &ndash; <strong>${formatDate(batch.completedAt)}</strong></div>
        <div>Ostolaskuja: <strong>${batch.purchaseInvoicesCount ?? 0} kpl</strong> &nbsp;/&nbsp; Myyntilaskuja: <strong>${batch.salesInvoicesCount ?? 0} kpl</strong></div>
      </div>
    </div>

    ${referenceHtml}

    <div class="divider"></div>

    ${areaSectionsHtml}

    ${summaryHtml}

    <div class="print-bar no-print">
      <button class="btn btn-primary" onclick="window.print()">Tulosta / Tallenna PDF</button>
      <button class="btn btn-secondary" onclick="window.close()">Sulje</button>
    </div>

  </div>
</body>
</html>`;

    const win = window.open("", "_blank");
    if (win) {
        win.document.write(html);
        win.document.close();
    }
}
